import type { ReactNode } from 'react';

import { TableCell, Typography, TypographyProps } from '@mui/material';

import { combineStyles } from '@/utils/styleUtils';

import type { TBaseCellProps } from '../types';
import { TruncatedTypography } from './TruncatedTypography';

export type TSimpleCellProps = TBaseCellProps & {
  value: ReactNode;
  typographyProps?: TypographyProps;
};

/**
 * Single-line body cell (no height set: the theme's 56px row minimum applies). A string or
 * number `value` without `children` truncates with a native tooltip when cut off
 * (`TruncatedTypography`); anything else renders in a plain `Typography` as before.
 */
export const SimpleCell = ({
  value,
  typographyProps,
  children,
  align,
  sx,
  ...props
}: TSimpleCellProps) => {
  const isEmpty = value === '';
  const isText = (typeof value === 'string' || typeof value === 'number') && !children;
  const Text = isText ? TruncatedTypography : Typography;

  return (
    <TableCell align={align} sx={sx} {...props}>
      <Text
        variant="body2"
        color={isEmpty ? 'text.disabled' : 'text.primary'}
        noWrap
        {...typographyProps}
        sx={combineStyles(
          align === 'right' && {
            width: '100%',
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            fontFeatureSettings: '"tnum"',
          },
          typographyProps?.sx,
        )}
      >
        {isEmpty ? '—' : value}
        {children}
      </Text>
    </TableCell>
  );
};
